export type IProgram = {
	id: string;
	_id: string;
	name: string;
};

export type BudgetLimitEnforcementMode = "WARN" | "BLOCK";

export interface BudgetConsumption {
	netEarned: number;
	budgetLimit: number;
	percentage: number;
}

export interface ProgramParameters {
	eligibility: boolean;
	consent: boolean;
	maxBeneficiaries: number;
	durationInWeeks: number;
	maxYearlyEarnings: number;
	budgetLimit: number;
	budgetLimitEnabled?: boolean;
	budgetLimitThreshold?: number;
	budgetLimitEnforcementMode?: BudgetLimitEnforcementMode;
	baseIncentiveNegative: number;
	baseIncentivePositive: number;
	biVisitIncrementNegative: number;
	repetitionPerSteps: number;
	eligibilityConfirmationIntervalDays?: number;
	_id?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	durationConfig?: any;
}

export interface JournalCrisisIntervention {
	enabled?: boolean;
	triggerMoods?: string[];
	title?: string;
	message?: string;
}

export interface CallConfiguration {
	allowCallRequests?: boolean;
	callCooldownMinutes?: number;
	expectedResponseHours?: number;
}

export interface ProgramFeatures {
	enableJournal?: boolean;
	enableRewards?: boolean;
	enableAutomatedNotifications?: boolean;
	enableActivityAlerts?: boolean;
	enableCallManagement?: boolean;
	// SIMS-4146: Daily Workflow feature flags for the Activity Sidebar.
	activitySidebar?: boolean;
	realtimeAlerts?: boolean;
	allowEnrollmentPause?: boolean;
	allowIncentiveInvalidation?: boolean;
	eventBasedRewards?: boolean;
	journalMultiPage?: boolean;
	journalCrisisIntervention?: JournalCrisisIntervention;
	callConfiguration?: CallConfiguration;
}

// SIMS-4146: per-program Activity Sidebar alert thresholds.
export interface ProgramActivityAlerts {
	delayedThresholdHours?: number;
	enableDelayedAlerts?: boolean;
	enableMissedAlerts?: boolean;
}

export interface ProgramVisibleTabs {
	showWeeklyCalendar?: boolean;
	showDailyJourney?: boolean;
	showClinicalInformation?: boolean;
}

export interface ProgramReportConfig {
	incentiveDisbursement?: boolean;
	educationalEngagement?: boolean;
	eventActivity?: boolean;
	eventRatings?: boolean;
}

export type LocalizedString = Record<string, string> | { def: string };

export enum EducationalContentType {
	VIDEO = "video",
	DOCUMENT = "document",
	LINK = "link",
}

export interface EducationalContentItem {
	id: string;
	type: EducationalContentType;
	title: LocalizedString;
	description: LocalizedString;
	category: string;
	thumbnailUrl: string;
	contentUrl: string;
	duration?: string;
	pageCount?: number;
	createdAt: string;
}

export interface Program {
	_id: string;
	id: string;
	accountId: string;
	name: string;
	type: "Stim" | "OUD";
	status: "ACTIVE" | "INACTIVE";
	multiTreatmentPlan: boolean;
	treatmentPlanChangeAllowed: boolean;
	treatmentPlan?: string;
	orgUnitId?: string[];
	parameters: ProgramParameters;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	budgetConsumption?: BudgetConsumption;
	cmRoles?: string[];
	additionalStaffRoles?: string[];
	payers?: string[];
	requirePayerPlanReviewOnReconfirm?: boolean;
	incentiveOnDemand?: boolean;
	onDemandIncentiveMin?: number;
	onDemandIncentiveMax?: number;
	features?: ProgramFeatures;
	activityAlerts?: ProgramActivityAlerts;
	visibleTabs?: ProgramVisibleTabs;
	reportConfig?: ProgramReportConfig;
	educationalContent?: EducationalContentItem[];
	newContentDays?: number;
}

export interface ProgramRequest {
	accountId: string;
	name: string;
	type: "Stim" | "OUD";
	status: "ACTIVE" | "INACTIVE";
	multiTreatmentPlan: boolean;
	treatmentPlanChangeAllowed: boolean;
	treatmentPlan?: string;
	orgUnitId?: string[];
	parameters: ProgramParameters;
	createdBy: string;
	cmRoles?: string[];
	additionalStaffRoles?: string[];
	payers?: string[];
	requirePayerPlanReviewOnReconfirm?: boolean;
	incentiveOnDemand?: boolean;
	onDemandIncentiveMin?: number;
	onDemandIncentiveMax?: number;
	features?: ProgramFeatures;
	activityAlerts?: ProgramActivityAlerts;
	visibleTabs?: ProgramVisibleTabs;
	reportConfig?: ProgramReportConfig;
	educationalContent?: EducationalContentItem[];
	newContentDays?: number;
}

export type Params = {
	accountId: string;
	userId?: string;
};

export enum IncentiveTypes {
	GC = "GC",
	PPC = "PPC",
}
